import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import Message from '../../components/ui/Message';
import Paginator from '../../components/ui/Paginator';
import { PageTour } from '../../components/ui/PageTour';
import { TourStep } from '../../components/ui/GuidedTour';
import { ProductStockModal } from './ProductStockModal';
import { exportProducts, IMPORT_LABELS, ImportType } from './productImport';
import { ProductsContext } from '../../context/products/ProductContext';
import { Menu, Product } from '../../models/interfaces';


const PAGE_SIZE = 20;

// Guía de la pantalla (se abre sola la primera vez; los elementos se marcan con data-tour)
const TOUR_STEPS: TourStep[] = [
    {
        key: 'intro', icon: 'bi-bag', title: 'Productos y menús',
        content: 'Aquí crea los productos que vende y los organiza en menús y submenús (por ejemplo: Bebidas › Calientes). Así aparecen en la caja y en el menú QR.',
    },
    {
        key: 'new-menu', target: '[data-tour="products-new-menu"]', icon: 'bi-folder-plus', title: '1. Cree sus menús',
        content: 'Empiece creando un menú para agrupar sus productos. Dentro de un menú puede crear submenús.',
    },
    {
        key: 'new-product', target: '[data-tour="products-new-product"]', icon: 'bi-plus-lg', title: '2. Agregue productos',
        content: 'Registre cada producto con su nombre, precio, impuestos e imagen, y elija el menú donde aparece.',
    },
    {
        key: 'import', target: '[data-tour="products-import-export"]', icon: 'bi-upload', title: 'Importar y exportar',
        content: 'Cargue muchos productos de una vez desde un archivo CSV (Excel), o descárguelos para revisarlos o editarlos.',
    },
    {
        key: 'tree', target: '[data-tour="products-tree"]', icon: 'bi-folder2', title: 'Filtrar por menú',
        content: 'Haga clic en un menú para ver solo sus productos. El número indica cuántos productos tiene.',
    },
    {
        key: 'search', target: '[data-tour="products-search"]', icon: 'bi-search', title: 'Buscar',
        content: 'Encuentre un producto por su nombre, código o el menú al que pertenece.',
    },
    {
        key: 'table', target: '[data-tour="products-table"]', icon: 'bi-pencil', title: 'Editar, eliminar o asociar inventario',
        content: 'En cada producto use el lápiz para editarlo, la papelera para eliminarlo o la caja para llevar su control en el inventario. Los botones en la fila del menú editan o eliminan el menú.',
    },
];

// Menú aplanado en orden jerárquico (recorrido en profundidad)
type Section = {
    menu: Menu;
    path: string[];        // nombres de los menús padre
    ancestorIds: number[]; // ids de los menús padre
    depth: number;
};

// Una fila paginable: un producto, o un menú sin productos (product = null)
type Item = {
    section: Section;
    product: Product | null;
};

// Valor que usa el formulario de producto para "cantidad sin definir"
const UNLIMITED_AVAILABLE = 1000000;

const formatNumber = (amount: number) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount ?? 0);

// Botón con menú desplegable (Importar / Exportar)
const DropdownButton = ({ label, icon, disabled, options }: {
    label: React.ReactNode;
    icon: string;
    disabled?: boolean;
    options: { key: string; label: string; description: string; icon: string; onClick: () => void }[];
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    return (
        <div className="position-relative" ref={ref}>
            <button type="button" className="btn btn-outline-secondary dropdown-toggle" disabled={disabled} onClick={() => setOpen(v => !v)}>
                <i className={`bi ${icon} me-1`} /> {label}
            </button>
            {open && (
                <ul className="dropdown-menu dropdown-menu-end show shadow-sm" style={{ position: 'absolute', right: 0, top: '100%', minWidth: 300, zIndex: 1050 }}>
                    {options.map(option => (
                        <li key={option.key}>
                            <button type="button" className="dropdown-item py-2" style={{ whiteSpace: 'normal' }} onClick={() => { setOpen(false); option.onClick(); }}>
                                <div className="fw-medium"><i className={`bi ${option.icon} me-2`} />{option.label}</div>
                                <div className="text-muted small">{option.description}</div>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const flattenSections = (menus: Menu[], path: string[] = [], ancestorIds: number[] = []): Section[] => {
    let result: Section[] = [];
    (menus || []).forEach(menu => {
        result.push({ menu, path, ancestorIds, depth: path.length });
        if (menu.submenu && menu.submenu.length > 0) {
            result = result.concat(flattenSections(menu.submenu, [...path, menu.name], [...ancestorIds, menu.id]));
        }
    });
    return result;
};

// Cantidad de productos del menú incluyendo sus submenús
const countProducts = (menu: Menu): number =>
    (menu.products?.length ?? 0) + (menu.submenu ?? []).reduce((acc, sub) => acc + countProducts(sub), 0);


export const ProductsPage = () => {

    const { products: menus, loadProducts, deleteMenu, deleteProduct, isLoading } = useContext(ProductsContext);

    const [buscar, setBuscar] = useState('');
    const [selectedMenuId, setSelectedMenuId] = useState<number | null>(null);
    const [expanded, setExpanded] = useState<Record<number, boolean>>({});
    const [pageActual, setPageActual] = useState<number>(1);

    const [error, setError] = useState('');
    const [confirm, setConfirm] = useState('');
    const [toDelete, setToDelete] = useState<Menu | Product | null>(null);
    const [success, setSuccess] = useState('');
    // Producto al que se le está asociando inventario (modal)
    const [stockProduct, setStockProduct] = useState<Product | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    // Exportación de productos en CSV (la importación tiene su propia pantalla)
    const [exporting, setExporting] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        loadProducts();
    }, []);

    // Resultado de la pantalla de importación: se muestra una vez y se limpia del historial
    useEffect(() => {
        const importMessage = (location.state as any)?.importMessage;
        if (importMessage) {
            setSuccess(importMessage);
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [location.state]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Vuelve a la primera página al cambiar búsqueda o menú seleccionado
    useEffect(() => {
        setPageActual(1);
    }, [buscar, selectedMenuId]);

    const sections = useMemo(() => flattenSections(menus), [menus]);

    const stats = useMemo(() => ({
        menus: sections.filter(s => s.depth === 0).length,
        submenus: sections.filter(s => s.depth > 0).length,
        products: sections.reduce((acc, s) => acc + (s.menu.products?.length ?? 0), 0),
    }), [sections]);

    // Filas visibles según menú seleccionado y búsqueda, respetando la jerarquía
    const items = useMemo<Item[]>(() => {
        const term = buscar.trim().toLowerCase();
        const result: Item[] = [];

        sections
            .filter(s => selectedMenuId === null || s.menu.id === selectedMenuId || s.ancestorIds.includes(selectedMenuId))
            .forEach(section => {
                const sectionProducts = section.menu.products ?? [];

                if (!term) {
                    if (sectionProducts.length === 0) {
                        result.push({ section, product: null });
                    } else {
                        sectionProducts.forEach(product => result.push({ section, product }));
                    }
                    return;
                }

                // Si el nombre del menú (o de un padre) coincide, se muestran todos sus productos
                const menuMatches = [...section.path, section.menu.name].some(name => name?.toLowerCase().includes(term));
                const matched = menuMatches
                    ? sectionProducts
                    : sectionProducts.filter(p =>
                        p.name?.toLowerCase().includes(term) ||
                        String(p.codeId ?? '').toLowerCase().includes(term));

                if (matched.length > 0) {
                    matched.forEach(product => result.push({ section, product }));
                } else if (menuMatches) {
                    result.push({ section, product: null });
                }
            });

        return result;
    }, [sections, buscar, selectedMenuId]);

    const totalRecords = items.length;
    const totalPage = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
    const pageStart = (pageActual - 1) * PAGE_SIZE;
    const pageItems = items.slice(pageStart, pageStart + PAGE_SIZE);
    const productCount = items.filter(i => i.product !== null).length;

    // Si al eliminar se reduce el número de páginas, se ajusta la página actual
    useEffect(() => {
        if (pageActual > totalPage) setPageActual(totalPage);
    }, [totalPage]);

    // =============================
    // Acciones
    // =============================
    // Vuelve a consultar los productos conservando búsqueda, menú seleccionado y página
    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await loadProducts();
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleExport = async (type: ImportType) => {
        setExporting(true);
        try {
            const { total, skipped } = await exportProducts(type);
            const recipes = skipped > 0
                ? ` ${skipped} producto(s) asociados a ítems del inventario con otro código (recetas o combos) no se incluyen; adminístrelos desde Inventario.`
                : '';
            if (total === 0) {
                setError(type === 'inventory'
                    ? `No hay productos con inventario asociado para exportar.${recipes}`
                    : 'No hay productos para exportar.');
            } else if (recipes) {
                setSuccess(`Se exportaron ${total} producto(s).${recipes}`);
            }
        } catch (e: any) {
            setError(e.message);
        } finally {
            setExporting(false);
        }
    };

    const handleNew = (opcion: string) => {
        navigate(opcion === "menu" ? "/menu" : "/product");
    };

    const handleEdit = (menuOrProduct: any) => {
        if ('parentId' in menuOrProduct) {
            navigate("/menu", { state: { menu: menuOrProduct } });
        } else if ('price' in menuOrProduct) {
            navigate("/product", { state: { product: menuOrProduct } });
        }
    };

    const handleStock = (product: Product) => {
        setStockProduct(product);
    };

    // Pantalla anterior: asociar productos que ya existen en el inventario
    const handleManageExistingStock = (product: Product) => {
        setStockProduct(null);
        navigate("/product-stock", { state: { product } });
    };

    const handleDelete = (menuOrProduct: Menu | Product) => {
        setToDelete(menuOrProduct);
        if ('parentId' in menuOrProduct) {
            const total = countProducts(menuOrProduct as Menu);
            setConfirm(total > 0
                ? `¿Está seguro que desea eliminar el menú "${menuOrProduct.name}"? Contiene ${total} producto(s).`
                : `¿Está seguro que desea eliminar el menú "${menuOrProduct.name}"?`);
        } else {
            setConfirm(`¿Está seguro que desea eliminar el producto "${menuOrProduct.name}"?`);
        }
    };

    const processDelete = async () => {
        setConfirm('');
        if (!toDelete) return;

        const isMenu = 'parentId' in toDelete;
        const resp = isMenu ? await deleteMenu(toDelete.id) : await deleteProduct(toDelete.id);

        if (resp !== "success") {
            setError(resp);
        } else if (isMenu && selectedMenuId === toDelete.id) {
            setSelectedMenuId(null);
        }
        setToDelete(null);
    };

    const toggleExpanded = (id: number) => {
        setExpanded(prev => ({ ...prev, [id]: !isExpanded(id, prev) }));
    };

    // Los menús principales inician expandidos
    const isExpanded = (id: number, state = expanded) => {
        if (id in state) return state[id];
        return sections.some(s => s.menu.id === id && s.depth === 0);
    };

    // =============================
    // Render
    // =============================
    const renderTree = (list: Menu[], depth: number = 0): JSX.Element[] =>
        (list || []).flatMap(menu => {
            const hasChildren = (menu.submenu?.length ?? 0) > 0;
            const open = isExpanded(menu.id);
            const active = selectedMenuId === menu.id;
            return [
                <li key={menu.id}>
                    <div
                        className={`d-flex align-items-center rounded pe-2 py-1 small ${active ? 'bg-primary-subtle text-primary-emphasis fw-semibold' : ''}`}
                        style={{ ...styles.treeItem, paddingLeft: 8 + depth * 16 }}
                        onClick={() => setSelectedMenuId(menu.id)}
                    >
                        <span
                            style={styles.treeToggle}
                            onClick={(e) => { e.stopPropagation(); if (hasChildren) toggleExpanded(menu.id); }}
                        >
                            {hasChildren && <i className={`bi ${open ? 'bi-chevron-down' : 'bi-chevron-right'}`} />}
                        </span>
                        <i className={`bi ${depth === 0 ? 'bi-folder2' : 'bi-folder'} me-2 text-muted`} />
                        <span className="flex-grow-1 text-truncate">{menu.name}</span>
                        <span className="badge rounded-pill bg-light text-muted border ms-1">{countProducts(menu)}</span>
                    </div>
                </li>,
                ...(hasChildren && open ? renderTree(menu.submenu!, depth + 1) : []),
            ];
        });

    const renderMenuActions = (menu: Menu) => (
        <>
            <button type="button" className="btn btn-sm btn-outline-primary" title="Editar menú" onClick={() => handleEdit(menu)}>
                <i className="bi bi-pencil" />
            </button>
            <button type="button" className="btn btn-sm btn-outline-danger ms-1" title="Eliminar menú" onClick={() => handleDelete(menu)}>
                <i className="bi bi-trash" />
            </button>
        </>
    );

    const renderRows = (): JSX.Element[] => {
        const rows: JSX.Element[] = [];
        let currentSectionId: number | null = null;

        pageItems.forEach((item, idx) => {
            const { section, product } = item;

            // Encabezado del menú cada vez que cambia la sección (o al inicio de la página)
            if (section.menu.id !== currentSectionId) {
                currentSectionId = section.menu.id;
                const continues = idx === 0 && pageStart > 0 && items[pageStart - 1].section.menu.id === section.menu.id;
                rows.push(
                    <tr key={`menu-${section.menu.id}`} className={section.depth === 0 ? 'table-light' : ''}>
                        <td colSpan={5} style={{ paddingLeft: 12 + section.depth * 24 }}>
                            <div className="d-flex align-items-center flex-wrap gap-1">
                                <i className={`bi ${section.depth === 0 ? 'bi-folder2-open' : 'bi-folder'} me-1 text-primary`} />
                                {section.path.map((name, i) => (
                                    <span key={i} className="text-muted small">{name} <i className="bi bi-chevron-right" style={{ fontSize: 10 }} /></span>
                                ))}
                                <span className={section.depth === 0 ? 'fw-bold fs-6' : 'fw-semibold'}>{section.menu.name}</span>
                                <span className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis ms-1">
                                    {section.menu.products?.length ?? 0} productos
                                </span>
                                {continues && <span className="text-muted small fst-italic ms-1">(continuación)</span>}
                            </div>
                        </td>
                        <td style={{ textAlign: 'right', width: 150, whiteSpace: 'nowrap' }}>{renderMenuActions(section.menu)}</td>
                    </tr>
                );
            }

            if (!product) {
                rows.push(
                    <tr key={`empty-${section.menu.id}`}>
                        <td colSpan={6} className="text-muted small fst-italic" style={{ paddingLeft: 40 + section.depth * 24 }}>
                            Este menú no tiene productos.
                        </td>
                    </tr>
                );
                return;
            }

            rows.push(
                <tr key={`product-${product.id}`}>
                    <td style={{ paddingLeft: 40 + section.depth * 24 }}>
                        <div className="fw-medium">{product.name}</div>
                        {product.shortDescription && <div className="text-muted small text-truncate" style={{ maxWidth: 380 }}>{product.shortDescription}</div>}
                    </td>
                    <td className="text-muted">{product.codeId || '-'}</td>
                    <td>{product.unit || '-'}</td>
                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                        {product.hasStock && (
                            <i className="bi bi-box-seam text-success me-1" title="Asociado al inventario" />
                        )}
                        {product.available === null || product.available === undefined || Number(product.available) >= UNLIMITED_AVAILABLE
                            ? <span className="text-muted small">Sin definir</span>
                            : <span className="fw-medium">{Number(product.available).toLocaleString('en-US')}</span>}
                    </td>
                    <td className="text-end fw-semibold">
                        {product.promoActive && <div className="small text-muted text-decoration-line-through fw-normal">{formatNumber(product.regularPrice)}</div>}
                        {formatNumber(product.price)}
                        {product.promoActive && <span className="badge rounded-pill bg-success-subtle text-success-emphasis ms-1">Oferta</span>}
                    </td>
                    <td style={{ textAlign: 'right', width: 150, whiteSpace: 'nowrap' }}>
                        {/* Solo se puede asociar inventario si el producto aún no tiene uno */}
                        {!product.hasStock && (
                            <button type="button" className="btn btn-sm btn-outline-secondary" title="Asociar inventario" onClick={() => handleStock(product)}>
                                <i className="bi bi-box-seam" />
                            </button>
                        )}
                        <button type="button" className="btn btn-sm btn-primary ms-1" title="Editar producto" onClick={() => handleEdit(product)}>
                            <i className="bi bi-pencil" />
                        </button>
                        <button type="button" className="btn btn-sm btn-danger ms-1" title="Eliminar producto" onClick={() => handleDelete(product)}>
                            <i className="bi bi-trash" />
                        </button>
                    </td>
                </tr>
            );
        });

        return rows;
    };

    const statCards = [
        { key: 'menus', label: 'Menús', value: stats.menus, icon: 'bi-folder2', color: '#0d6efd', bg: '#e7f1ff' },
        { key: 'submenus', label: 'Submenús', value: stats.submenus, icon: 'bi-diagram-3', color: '#6f42c1', bg: '#f0ebfa' },
        { key: 'products', label: 'Productos', value: stats.products, icon: 'bi-bag', color: '#198754', bg: '#e8f6ee' },
    ];

    const startIndex = totalRecords === 0 ? 0 : pageStart + 1;
    const endIndex = Math.min(pageActual * PAGE_SIZE, totalRecords);

    const removeLocalError = () => setError('');
    const removeConfirmMessage = () => { setConfirm(''); setToDelete(null); };

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Productos y menús</h3>
                    <p className="text-muted mb-0">Organice sus productos por menú y submenú.</p>
                </div>
                <div className="d-flex flex-wrap gap-2 mt-2 mt-md-0">
                    <PageTour id="products" steps={TOUR_STEPS} />
                    <button type="button" className="btn btn-outline-secondary" onClick={handleRefresh} disabled={isRefreshing || isLoading} title="Volver a consultar los datos de esta pantalla">
                        {isRefreshing
                            ? <><span className="spinner-border spinner-border-sm me-1" /> Actualizando...</>
                            : <><i className="bi bi-arrow-clockwise me-1" /> Actualizar</>}
                    </button>
                    <div className="d-flex flex-wrap gap-2" data-tour="products-import-export">
                    <DropdownButton
                        label="Importar"
                        icon="bi-upload"
                        options={(['products', 'inventory'] as ImportType[]).map(type => ({
                            key: type,
                            label: IMPORT_LABELS[type].short,
                            description: IMPORT_LABELS[type].description,
                            icon: type === 'inventory' ? 'bi-box-seam' : 'bi-bag',
                            onClick: () => navigate(`/products/import?type=${type}`),
                        }))}
                    />
                    <DropdownButton
                        label={exporting ? <><span className="spinner-border spinner-border-sm me-1" />Exportando...</> : 'Exportar'}
                        icon="bi-download"
                        disabled={exporting}
                        options={[
                            { key: 'products', label: 'Productos', description: 'Todos los productos, con la estructura de la plantilla de importación.', icon: 'bi-bag', onClick: () => handleExport('products') },
                            { key: 'inventory', label: 'Productos con inventario', description: 'Los productos asociados al inventario, con su stock, costo y proveedor.', icon: 'bi-box-seam', onClick: () => handleExport('inventory') },
                        ]}
                    />
                    </div>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/products/modifiers')} title="Opciones que se preguntan al vender: término, ingredientes, extras">
                        <i className="bi bi-ui-checks-grid me-1" /> Modificadores
                    </button>
                    <button type="button" className="btn btn-outline-secondary" data-tour="products-new-menu" onClick={() => handleNew('menu')}>
                        <i className="bi bi-folder-plus me-1" /> Nuevo menú
                    </button>
                    <button type="button" className="btn btn-primary" data-tour="products-new-product" onClick={() => handleNew('producto')}>
                        <i className="bi bi-plus-lg me-1" /> Nuevo producto
                    </button>
                </div>
            </div>

            {/* Tarjetas de resumen */}
            <div className="row g-3 mb-4">
                {statCards.map(card => (
                    <div className="col-12 col-sm-4" key={card.key}>
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-body d-flex align-items-center gap-3">
                                <div style={{ ...styles.iconCircle, backgroundColor: card.bg, color: card.color }}>
                                    <i className={`bi ${card.icon}`} />
                                </div>
                                <div>
                                    <div className="text-muted small">{card.label}</div>
                                    <div className="fs-4 fw-semibold">{card.value}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="row g-3">
                {/* Árbol de menús */}
                <div className="col-12 col-lg-3">
                    <div className="card border-0 shadow-sm" data-tour="products-tree">
                        <div className="card-body">
                            <h6 className="mb-3">Menús</h6>
                            <ul className="list-unstyled mb-0">
                                <li>
                                    <div
                                        className={`d-flex align-items-center rounded px-2 py-1 small ${selectedMenuId === null ? 'bg-primary-subtle text-primary-emphasis fw-semibold' : ''}`}
                                        style={styles.treeItem}
                                        onClick={() => setSelectedMenuId(null)}
                                    >
                                        <span style={styles.treeToggle} />
                                        <i className="bi bi-grid me-2 text-muted" />
                                        <span className="flex-grow-1">Todos</span>
                                        <span className="badge rounded-pill bg-light text-muted border ms-1">{stats.products}</span>
                                    </div>
                                </li>
                                {renderTree(menus)}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Listado jerárquico */}
                <div className="col-12 col-lg-9">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">

                            <div className="row g-2 mb-3">
                                <div className="col-md-8">
                                    <div className="input-group" data-tour="products-search">
                                        <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                        <input
                                            className="form-control"
                                            type="text"
                                            placeholder="Buscar por producto, código o menú..."
                                            value={buscar}
                                            onChange={(e) => setBuscar(e.target.value)}
                                        />
                                        {buscar && (
                                            <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setBuscar('')}>
                                                <i className="bi bi-x-lg" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="table-responsive" data-tour="products-table">
                                <table className="table table-hover align-middle">
                                    <thead>
                                        <tr className="text-muted small text-uppercase">
                                            <th>Producto</th>
                                            <th>Código</th>
                                            <th>Unidad</th>
                                            <th className="text-end">Cantidad</th>
                                            <th className="text-end">Precio</th>
                                            <th style={{ textAlign: 'right' }}>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            <tr><td colSpan={6} className='text-center py-4'>
                                                <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                                            </td></tr>
                                        ) : pageItems.length > 0 ? (
                                            renderRows()
                                        ) : (
                                            <tr><td colSpan={6} className='text-center py-4 text-muted'>
                                                {buscar ? 'No se encontraron productos para la búsqueda.' : 'No se encontró un menú/producto creado.'}
                                            </td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                                <span className="text-muted small">
                                    {totalRecords > 0 ? `Mostrando ${startIndex}-${endIndex} de ${totalRecords} filas · ${productCount} productos` : ''}
                                </span>
                                {totalPage > 1 ? <Paginator totalPage={totalPage} pageActual={pageActual} onChange={(actual) => setPageActual(actual)} /> : <div />}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }} />

            {error.length > 0 && (
                <Message
                    type="error"
                    message={error}
                    closeDialog={removeLocalError}
                    handleOk={removeLocalError}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}

            {stockProduct && (
                <ProductStockModal
                    product={stockProduct}
                    onClose={() => setStockProduct(null)}
                    onSaved={(message) => { setStockProduct(null); setSuccess(message); loadProducts(); }}
                    onManageExisting={() => handleManageExistingStock(stockProduct)}
                />
            )}

            {success.length > 0 && (
                <Message
                    type="success"
                    title="Exitoso"
                    message={success}
                    closeDialog={() => setSuccess('')}
                    handleOk={() => setSuccess('')}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}

            {confirm.length > 0 && (
                <Message
                    type="success"
                    title="Atención"
                    message={confirm}
                    closeDialog={removeConfirmMessage}
                    showCancel={true}
                    handleCancel={removeConfirmMessage}
                    handleOk={processDelete}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
        </div>
    );
};


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
    iconCircle: {
        width: 48,
        height: 48,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 20,
        flexShrink: 0,
    },
    treeItem: {
        cursor: 'pointer',
        userSelect: 'none' as const,
    },
    treeToggle: {
        width: 16,
        display: 'inline-flex',
        justifyContent: 'center',
        marginRight: 4,
        color: '#6c757d',
    },
};
